import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Upload } from 'lucide-react';
import { ambAxios, getAmbToken, clearAmbToken, C, apiError } from './ambApi';
import { Header, AmbFooter } from './AmbassadorResources';

export default function AmbassadorSettings() {
  const navigate = useNavigate();
  const fileRef = useRef();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [f, setF] = useState({});
  const [pw, setPw] = useState({ current_password: '', new_password: '' });

  useEffect(() => {
    if (!getAmbToken()) { navigate('/ambassador/login'); return; }
    ambAxios().get('/ambassador/me').then((r) => { setA(r.data); setF(r.data); })
      .catch((e) => { if (e?.response?.status === 401) { clearAmbToken(); navigate('/ambassador/login'); } })
      .finally(() => setLoading(false));
  }, [navigate]);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#F7F8FA]"><Loader2 className="h-8 w-8 animate-spin" style={{ color: C.navy }} /></div>;
  if (!a) return null;

  const saveProfile = async () => {
    try {
      const r = await ambAxios().put('/ambassador/profile', {
        first_name: f.first_name, last_name: f.last_name, phone: f.phone,
        payout_email: f.payout_email, payout_method: f.payout_method,
        organization: f.organization, social_links: f.social_links,
      });
      setA(r.data); toast.success('Profile saved');
    } catch (e) { toast.error(apiError(e)); }
  };
  const changePw = async () => {
    try { await ambAxios().post('/ambassador/change-password', pw); toast.success('Password changed'); setPw({ current_password: '', new_password: '' }); }
    catch (e) { toast.error(apiError(e)); }
  };
  const uploadPic = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) { toast.error('JPG/PNG/WebP only'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    const fd = new FormData(); fd.append('profile_picture', file);
    try {
      const r = await ambAxios().post('/ambassador/profile-picture', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setA({ ...a, profile_picture_url: r.data.profile_picture_url, profile_picture_status: 'pending_review' });
      toast.success('Photo uploaded — pending admin review');
    } catch (err) { toast.error(apiError(err)); }
  };
  const inp = (k, label, type = 'text') => (
    <div><label className="block text-xs font-semibold mb-1" style={{ color: '#57534E' }}>{label}</label>
      <input type={type} value={f[k] || ''} onChange={(e) => setF({ ...f, [k]: e.target.value })}
        className="w-full h-11 px-3 rounded-xl border border-[#E3E1DC] text-sm outline-none" data-testid={`amb-set-${k}`} /></div>
  );

  const connectStripe = async () => {
    try { const r = await ambAxios().post('/ambassador/stripe/connect-link'); window.location.href = r.data.url; }
    catch (e) { toast.error(apiError(e)); }
  };
  const refreshStripe = async () => {
    try { const r = await ambAxios().get('/ambassador/stripe/status'); setA((prev) => ({ ...prev, stripe_account_id: r.data.stripe_account_id, stripe_payouts_enabled: r.data.payouts_enabled, stripe_details_submitted: r.data.details_submitted })); toast.success('Status refreshed'); }
    catch (e) { toast.error(apiError(e)); }
  };
  const payoutsReady = a.stripe_payouts_enabled;

  return (
    <div className="min-h-screen bg-[#F7F8FA] overflow-x-hidden" data-testid="ambassador-settings">
      <Header navigate={navigate} active="settings" />
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        <div className="bg-white rounded-2xl border border-[#E8EAEE] p-5">
          <p className="font-semibold mb-3" style={{ color: C.ink }}>Profile picture</p>
          <div className="flex items-center gap-4">
            {a.profile_picture_url ? <img src={a.profile_picture_url} alt="" className="w-20 h-20 rounded-full object-cover" />
              : <div className="w-20 h-20 rounded-full flex items-center justify-center text-white text-xl" style={{ background: C.navy2 }}>{(a.first_name || '?')[0]}</div>}
            <div>
              <button onClick={() => fileRef.current?.click()} className="text-sm px-4 h-10 rounded-full border flex items-center gap-2" style={{ borderColor: C.line, color: C.navy }} data-testid="amb-upload-pic"><Upload className="h-4 w-4" /> Upload new</button>
              <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadPic} className="hidden" />
              {a.profile_picture_status && <p className="text-xs mt-1" style={{ color: C.mute }}>Status: {a.profile_picture_status}</p>}
            </div>
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-[#E8EAEE] p-5 space-y-3">
          <p className="font-semibold" style={{ color: C.ink }}>Profile details</p>
          <div className="grid grid-cols-2 gap-3">{inp('first_name', 'First name')}{inp('last_name', 'Last name')}</div>
          {inp('phone', 'Phone')}
          {inp('organization', 'Organization / business')}
          {inp('social_links', 'Social media links')}
          <button onClick={saveProfile} className="h-11 px-6 rounded-xl text-white font-semibold" style={{ background: C.navy }} data-testid="amb-save-profile">Save profile</button>
        </div>
        <div className="bg-white rounded-2xl border border-[#E8EAEE] p-5 space-y-3" data-testid="amb-stripe-payouts">
          <p className="font-semibold" style={{ color: C.ink }}>Payouts</p>
          <p className="text-sm" style={{ color: C.mute }}>We pay your commissions securely through Stripe. Connect your bank once — Stripe handles your details, we never see them.</p>
          {payoutsReady ? (
            <div className="flex items-center gap-2 text-sm font-semibold p-3 rounded-xl" style={{ background: '#EAF3EE', color: '#2E7D5B' }} data-testid="amb-stripe-ready">
              ✓ Your Stripe payout account is connected and ready.
            </div>
          ) : a.stripe_account_id ? (
            <div className="space-y-2">
              <div className="text-sm p-3 rounded-xl" style={{ background: '#FBF3E4', color: '#8A6D1F' }}>Onboarding started but not finished. Complete it to receive payouts.</div>
              <div className="flex gap-2">
                <button onClick={connectStripe} className="h-11 px-6 rounded-xl text-white font-semibold" style={{ background: C.navy }} data-testid="amb-stripe-continue">Finish Stripe setup</button>
                <button onClick={refreshStripe} className="h-11 px-5 rounded-xl border font-semibold" style={{ borderColor: C.line, color: C.navy }} data-testid="amb-stripe-refresh">Refresh status</button>
              </div>
            </div>
          ) : (
            <button onClick={connectStripe} className="h-11 px-6 rounded-xl text-white font-semibold" style={{ background: C.navy }} data-testid="amb-stripe-connect">Set up payouts with Stripe</button>
          )}
        </div>
        <div className="bg-white rounded-2xl border border-[#E8EAEE] p-5 space-y-3">
          <p className="font-semibold" style={{ color: C.ink }}>Change password</p>
          <input type="password" placeholder="Current password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-[#E3E1DC] text-sm outline-none" />
          <input type="password" placeholder="New password (min 8 chars)" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="w-full h-11 px-3 rounded-xl border border-[#E3E1DC] text-sm outline-none" />
          <button onClick={changePw} className="h-11 px-6 rounded-xl text-white font-semibold" style={{ background: C.navy }} data-testid="amb-change-pw">Update password</button>
        </div>
      </div>
      <AmbFooter />
    </div>
  );
}
